import { Crown, Heart, MessageCircle, Clock, Gift } from 'lucide-react';
import { Theme } from '@/shared/constants/themes';

interface FollowerStats {
    nickname: string;
    score: number;
    messages: number;
    presenceMs: number;
    likes: number;
    gifts: number;
}

interface LiveFollowersProps {
    followers?: FollowerStats[];
    theme?: Theme;
}

export const LiveFollowers: React.FC<LiveFollowersProps> = ({ followers = [], theme }) => {
    const themeColors = theme?.colors;

    const formatPresence = (ms: number) => {
        const minutes = Math.floor(ms / 60000);
        if (minutes < 1) return '< 1m';
        if (minutes > 60) return `${Math.floor(minutes / 60)}h${minutes % 60}m`;
        return `${minutes}m`;
    };

    return (
        <div 
            className="rounded-lg shadow-sm border overflow-hidden"
            style={{
                backgroundColor: themeColors?.surface || '#FFFFFF',
                borderColor: themeColors?.border || '#E5E7EB'
            }}
        >
            {/* Header */}
            <div 
                className="px-4 py-3 border-b"
                style={{
                    background: `linear-gradient(to right, ${themeColors?.warningLight || '#FEF3C7'}20, ${themeColors?.warning || '#F59E0B'}15)`,
                    borderBottomColor: themeColors?.border || '#E5E7EB'
                }}
            >
                <div className="flex items-center gap-2">
                    <Crown 
                        size={16} 
                        style={{ color: themeColors?.warning || '#F59E0B' }}
                    />
                    <h3 
                        className="text-sm font-semibold"
                        style={{ color: themeColors?.foreground || '#374151' }}
                    >
                        Top Followers
                    </h3>
                </div>
            </div>

            {/* Followers List */}
            <div className="p-3 space-y-2">
                {followers.length === 0 ? (
                    <p 
                        className="text-xs italic text-center py-6"
                        style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                    >
                        En attente de données...
                    </p>
                ) : (
                    followers.map((follower, idx) => (
                        <div
                            key={idx}
                            className="rounded-lg p-2.5 border transition-all"
                            style={{
                                backgroundColor: themeColors?.backgroundSecondary || '#F9FAFB',
                                borderColor: themeColors?.border || '#E5E7EB'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.warning || '#F59E0B';
                                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = themeColors?.border || '#E5E7EB';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            {/* Header: Name & Score */}
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <div 
                                        className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
                                        style={{
                                            background: `linear-gradient(to bottom right, ${themeColors?.warning || '#F59E0B'}, ${themeColors?.warningDark || '#D97706'})`
                                        }}
                                    >
                                        {follower.nickname.substring(0, 2).toUpperCase()}
                                    </div>
                                    <span 
                                        className="font-semibold text-xs truncate" 
                                        title={follower.nickname}
                                        style={{ color: themeColors?.foreground || '#111827' }}
                                    >
                                        {follower.nickname}
                                    </span>
                                </div>
                                <div 
                                    className="flex items-center gap-1 px-2 py-0.5 rounded-full ml-2"
                                    style={{
                                        backgroundColor: themeColors?.secondaryLight || '#F9A8D4',
                                        color: themeColors?.secondaryDark || '#9F1239'
                                    }}
                                >
                                    <Heart size={10} fill="currentColor" />
                                    <span className="text-[10px] font-bold">{follower.score}</span>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-3 gap-1.5">
                                <div 
                                    className="flex items-center gap-1 px-1.5 py-1 rounded border"
                                    style={{
                                        backgroundColor: themeColors?.surface || '#FFFFFF',
                                        borderColor: themeColors?.borderLight || '#F3F4F6'
                                    }}
                                >
                                    <MessageCircle size={10} style={{ color: themeColors?.primary || '#9333EA' }} />
                                    <span 
                                        className="text-[10px] font-medium"
                                        style={{ color: themeColors?.foreground || '#374151' }}
                                    >
                                        {follower.messages}
                                    </span>
                                </div>
                                <div 
                                    className="flex items-center gap-1 px-1.5 py-1 rounded border"
                                    style={{
                                        backgroundColor: themeColors?.surface || '#FFFFFF',
                                        borderColor: themeColors?.borderLight || '#F3F4F6'
                                    }}
                                >
                                    <Clock size={10} style={{ color: themeColors?.success || '#10B981' }} />
                                    <span 
                                        className="text-[10px] font-medium"
                                        style={{ color: themeColors?.foreground || '#374151' }}
                                    >
                                        {formatPresence(follower.presenceMs)}
                                    </span>
                                </div>
                                {follower.gifts > 0 && (
                                    <div 
                                        className="flex items-center gap-1 px-1.5 py-1 rounded border"
                                        style={{
                                            backgroundColor: themeColors?.surface || '#FFFFFF',
                                            borderColor: themeColors?.borderLight || '#F3F4F6'
                                        }}
                                    >
                                        <Gift size={10} style={{ color: themeColors?.secondary || '#EC4899' }} />
                                        <span 
                                            className="text-[10px] font-medium"
                                            style={{ color: themeColors?.foreground || '#374151' }}
                                        >
                                            {follower.gifts}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
